import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSample } from "../SampleContext";
import { useLayout } from "../LayoutContext";

interface AgentGeneratorProps {
    setScreen: (screen: "intro" | "recorder" | "uploader" | "form") => void;
}

const languages: { code: string; name: string }[] = [
    { code: 'en', name: 'English (United States)' },
    { code: 'en', name: 'English (United Kingdom)' },
    { code: 'en', name: 'English (Australia)' },
    { code: 'en', name: 'English (India)' },
    { code: 'es', name: 'Spanish (Spain)' },
    { code: 'es', name: 'Spanish (Mexico)' },
    { code: 'es', name: 'Spanish (Argentina)' },
    { code: 'fr', name: 'French (France)' },
    { code: 'fr', name: 'French (Canada)' },
    { code: 'pt', name: 'Portuguese (Portugal)' },
    { code: 'pt', name: 'Portuguese (Brazil)' },
    { code: 'zh', name: 'Chinese (Simplified)' },
    { code: 'zh', name: 'Chinese (Traditional)' },
    { code: 'de', name: 'German' },
    { code: 'ru', name: 'Russian' },
    { code: 'uk', name: 'Ukrainian' },
    { code: 'ar', name: 'Arabic (Saudi Arabia)' },
    { code: 'ar', name: 'Arabic (Egypt)' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'hi', name: 'Hindi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'tr', name: 'Turkish' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' },
    { code: 'pl', name: 'Polish' },
    { code: 'th', name: 'Thai' },
    { code: 'he', name: 'Hebrew' },
    { code: 'id', name: 'Indonesian' },
    { code: 'ms', name: 'Malay' },
    { code: 'fa', name: 'Persian (Farsi)' },
    { code: 'sv', name: 'Swedish' },
    { code: 'fi', name: 'Finnish' },
    { code: 'no', name: 'Norwegian' },
    { code: 'da', name: 'Danish' },
    { code: 'cs', name: 'Czech' },
    { code: 'ro', name: 'Romanian' },
    { code: 'el', name: 'Greek' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'mr', name: 'Marathi' },
    { code: 'ur', name: 'Urdu (Pakistan)' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'sr', name: 'Serbian' },
    { code: 'hr', name: 'Croatian' },
    { code: 'sk', name: 'Slovak' },
    { code: 'bg', name: 'Bulgarian' },
    { code: 'lt', name: 'Lithuanian' },
    { code: 'lv', name: 'Latvian' },
    { code: 'et', name: 'Estonian' },
    { code: 'sl', name: 'Slovenian' },
    { code: 'af', name: 'Afrikaans' },
    { code: 'sw', name: 'Swahili' },
    { code: 'am', name: 'Amharic' },
    { code: 'yo', name: 'Yoruba' },
    { code: 'ig', name: 'Igbo' },
    { code: 'zu', name: 'Zulu' },
    { code: 'xh', name: 'Xhosa' },
    { code: 'ne', name: 'Nepali' },
    { code: 'si', name: 'Sinhala' },
    { code: 'my', name: 'Burmese' },
    { code: 'km', name: 'Khmer' },
    { code: 'lo', name: 'Lao' },
    { code: 'mn', name: 'Mongolian' },
    { code: 'ka', name: 'Georgian' },
    { code: 'hy', name: 'Armenian' },
    { code: 'kk', name: 'Kazakh' },
    { code: 'uz', name: 'Uzbek' },
    { code: 'az', name: 'Azerbaijani' },
    { code: 'ps', name: 'Pashto' },
    { code: 'tk', name: 'Turkmen' },
];

type Links = Record<string, string>;
const isValidHexColor = (c: string): boolean => /^#([0-9A-F]{3}){1,2}$/i.test(c);

const AgentGenerator: React.FC<AgentGeneratorProps> = ({ setScreen }) => {
    const { msp, account } = useParams<{ msp?: string; account?: string }>();
    const { layout } = useLayout();
    const { recordedBlob, uploadedFiles, setRecordedBlob, setUploadedFiles, setSampleDuration } = useSample();

    const [name, setName] = useState("");
    const [languageName, setLanguageName] = useState<string>("");     // for display (e.g. "English (United States)")
    const [languageCode, setLanguageCode] = useState<string>("");     // required, e.g. "en-US"
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ name?: string; file?: string; language?: string }>({});
    const [links, setLinks] = useState<Links | null>(null);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listDivRef = useRef<HTMLDivElement>(null);

    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : "#22FFAF",
        text: isValidHexColor(layout.color.text) ? layout.color.text : "#000000",
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : "#1DE9A3",
    };

    const hasMedia = !!(uploadedFiles?.length || recordedBlob);

    const filteredLanguages = languages.filter((lang) =>
        lang.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleLanguageSelect = (lang: { code: string; name: string }) => {
        setLanguageCode(lang.code);
        setLanguageName(lang.name);
        setSearchQuery(lang.name);
        setIsDropdownOpen(false);
    };

    const validate = () => {
        const e: { name?: string; file?: string; language?: string } = {};
        if (!name.trim()) e.name = "Name is required";
        if (!hasMedia) e.file = "No audio available (upload or record)";
        if (!languageCode) e.language = "Please pick a language";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    useEffect(() => {
        validate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, recordedBlob, uploadedFiles, languageCode]);

    const handleSubmit = async () => {
        if (!validate()) {
            alert("Please fix the form errors.");
            return;
        }

        try {
            setIsSubmitting(true);
            const fd = new FormData();

            // Prefer uploaded file, fallback to recorded blob
            if (uploadedFiles && uploadedFiles.length > 0) {
                const f = uploadedFiles[0];
                fd.append("mp3File", f, f.name || "upload.mp3");
            } else if (recordedBlob) {
                const fallbackName = recordedBlob.type?.startsWith("video/")
                    ? "recording.webm"
                    : recordedBlob.type === "audio/wav"
                        ? "recording.wav"
                        : "recording.mp3";
                fd.append("mp3File", recordedBlob, fallbackName);
            } else {
                setErrors((e) => ({ ...e, file: "No audio available" }));
                return;
            }

            fd.append("name", name);
            fd.append("agentLanguage", languageCode);
            if (msp?.trim()) fd.append("msp", msp);
            if (account?.trim()) fd.append("account", account);

            const res = await fetch("/api/create-voice-agent", { method: "POST", body: fd });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e?.details || e?.error || `HTTP ${res.status}`);
            }

            const data = await res.json();
            if (!data?.success || !data?.links) throw new Error("Unexpected API response shape");
            setLinks(data.links);

            setRecordedBlob(null);
            setUploadedFiles(null);
            setSampleDuration(0);
        } catch (err: any) {
            console.error("Submit error:", err);
            alert(`Failed to create voice/agent: ${err.message || "Unknown error"}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopy = async (key: string, url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 1500);
        } catch { }
    };

    const resetToForm = () => {
        setLinks(null);
        setName("");
        setLanguageCode("");
        setLanguageName("");
        setSearchQuery("");
        setRecordedBlob(null);
        setUploadedFiles(null);
        setSampleDuration(0);
    };

    if (links) {
        return (
            <div className="w-full max-w-[520px] p-6 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center">
                <h1 className={`text-[30px] font-bold text-[${safeColors.buttons}] mb-5`}>Agent Created</h1>
                <div className="w-full space-y-4">
                    {Object.entries(links).map(([key, url]) => (
                        <div key={key} className="w-full">
                            <label className="block text-sm text-white font-medium mb-1">{key}</label>
                            <div className="flex gap-2">
                                <a className={`text-[${safeColors.buttons}] text-[20px] max-w-[245px] truncate`} href={url}>{url}</a>
                                <button
                                    className={`rounded-md px-3 py-2 font-bold bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}]`}
                                    onClick={() => handleCopy(key, url)}
                                >
                                    {copiedKey === key ? "Copied!" : "Copy"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex gap-6 mt-6">
                    <button className="bg-[#313131] hover:bg-[#5a5a5a] rounded-lg px-4 py-[11px] font-bold text-white" onClick={() => setScreen("intro")}>
                        Back to Intro
                    </button>
                    <button className={`rounded-lg px-4 py-[11px] font-bold bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}]`} onClick={resetToForm}>
                        Create another
                    </button>
                </div>
            </div>
        );
    }

    // Form view
    return (
        <div className="w-full max-w-[420px] p-6 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center">
            <h1 className={`text-[30px] font-bold text-[${safeColors.buttons}] mb-5`}>Create Voice Agent</h1>

            <div className="mb-4 w-full">
                <label className="block text-sm text-white font-medium">Name*</label>
                <input
                    type="text"
                    className={`mt-1 p-2 w-full border border-solid rounded-md bg-[#313131] text-white placeholder:text-gray-400 border-[${safeColors.buttons}]`}
                    placeholder="e.g., Alex Morgan"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </div>

            <div className="mb-4 w-full relative">
                <label className="block text-sm text-white font-medium">Your Language*</label>
                <input
                    type="text"
                    ref={searchInputRef}
                    className={`mt-1 p-2 w-full bg-[#313131] text-white placeholder:text-gray-400 border border-solid rounded-md border-[${safeColors.buttons}] ${isDropdownOpen && filteredLanguages.length > 0 ? "rounded-t-md" : "rounded-md"}`}
                    placeholder="Type to search…"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setLanguageCode("");
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                />
                <div
                    ref={listDivRef}
                    className={`absolute w-full max-h-40 overflow-y-auto bg-[#1A1A1A] text-white border border-[${safeColors.buttons}] border-t-0 rounded-b-md ${isDropdownOpen && filteredLanguages.length > 0 ? "block" : "hidden"}`}
                >
                    {filteredLanguages.map((lang) => (
                        <div
                            key={lang.code}
                            className={`px-2 py-1 cursor-pointer hover:bg-[${safeColors.buttons}] hover:text-[${safeColors.text}]`}
                            onClick={() => handleLanguageSelect(lang)}
                        >
                            {lang.name}
                        </div>
                    ))}
                </div>
                {!languageCode && <p className="text-xs text-red-500 mt-1">Please select a language from the list.</p>}
            </div>

            <div className="flex gap-6">
                <button
                    className="bg-[#313131] hover:bg-[#5a5a5a] rounded-lg px-4 py-[11px] font-bold text-white"
                    onClick={() => {
                        setScreen("intro");
                        resetToForm();
                    }}
                >
                    Start over
                </button>

                <button
                    className={`rounded-lg px-4 py-[11px] font-bold bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}] ${isSubmitting || !name.trim() || !hasMedia || !languageCode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                        }`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || !name.trim() || !hasMedia || !languageCode}
                >
                    {isSubmitting ? "Creating…" : "Create Agent"}
                </button>
            </div>
        </div>
    );
};

export default AgentGenerator;